import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import permissionsService from "../services/permissionsService";
import styles from "./crmPermissions.module.css";

const CrmPermissions = () => {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Separate state for each table
  const [categorySearchTerm, setCategorySearchTerm] = useState("");
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");
  const [categoryEntriesPerPage, setCategoryEntriesPerPage] = useState(30);
  const [permissionEntriesPerPage, setPermissionEntriesPerPage] = useState(30);
  const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
  const [permissionCurrentPage, setPermissionCurrentPage] = useState(1);
  const [categorySortConfig, setCategorySortConfig] = useState({ key: null, direction: null });
  const [permissionSortConfig, setPermissionSortConfig] = useState({ key: null, direction: null });

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await permissionsService.getPermissions();
      
      if (response.success) {
        setPermissions(response.data?.permissions || []);
        setCategories(response.data?.permissionCategories || []);
      } else {
        // Fallback to mock data with more entries to show pagination
        const mockCategories = [
          { id: 1, name: "Users", permissions_count: 0 },
          { id: 2, name: "Guardians", permissions_count: 15 },
          { id: 3, name: "Finance", permissions_count: 6 },
          { id: 4, name: "Properties", permissions_count: 4 },
          { id: 5, name: "Clients", permissions_count: 10 },
          { id: 6, name: "Tickets", permissions_count: 0 },
          { id: 7, name: "Contractors", permissions_count: 7 },
          { id: 8, name: "Marketing", permissions_count: 1 },
          { id: 9, name: "Quality", permissions_count: 0 },
          { id: 10, name: "Menu", permissions_count: 2 }
        ];
        
        // Generate more permissions to show pagination
        const mockPermissions = [];
        for (let i = 1; i <= 133; i++) {
          mockPermissions.push({
            id: 2450 + i,
            display_name: `Permission ${i}`,
            name: `permission.${i}`
          });
        }
        
        setPermissions(mockPermissions);
        setCategories(mockCategories);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleShowUsers = (categoryId) => {
    navigate(`/permissions/category/${categoryId}/users`);
  };

  const handleEditPermission = (permissionId) => {
    navigate(`/permissions/${permissionId}/edit`);
  };

  const handleCategorySearch = (e) => {
    setCategorySearchTerm(e.target.value);
    setCategoryCurrentPage(1);
  };

  const handlePermissionSearch = (e) => {
    setPermissionSearchTerm(e.target.value);
    setPermissionCurrentPage(1);
  };

  const handleCategoryEntriesChange = (e) => {
    setCategoryEntriesPerPage(parseInt(e.target.value));
    setCategoryCurrentPage(1);
  };

  const handlePermissionEntriesChange = (e) => {
    setPermissionEntriesPerPage(parseInt(e.target.value));
    setPermissionCurrentPage(1);
  };

  const handleCategorySort = (key) => {
    let direction = "asc";
    if (categorySortConfig.key === key && categorySortConfig.direction === "asc") {
      direction = "desc";
    }
    setCategorySortConfig({ key, direction });
  };

  const handlePermissionSort = (key) => {
    let direction = "asc";
    if (permissionSortConfig.key === key && permissionSortConfig.direction === "asc") {
      direction = "desc";
    }
    setPermissionSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey, sortConfig) => {
    if (sortConfig.key !== columnKey) {
      return "⇅";
    }
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  // Filter categories based on search term
  let filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  // Apply sorting to categories
  if (categorySortConfig.key) {
    filteredCategories = [...filteredCategories].sort((a, b) => {
      let aValue = a[categorySortConfig.key];
      let bValue = b[categorySortConfig.key];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return categorySortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return categorySortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  // Filter permissions based on search term
  let filteredPermissions = permissions.filter(permission =>
    permission.display_name.toLowerCase().includes(permissionSearchTerm.toLowerCase()) ||
    permission.name.toLowerCase().includes(permissionSearchTerm.toLowerCase())
  );

  // Apply sorting to permissions
  if (permissionSortConfig.key) {
    filteredPermissions = [...filteredPermissions].sort((a, b) => {
      let aValue = a[permissionSortConfig.key];
      let bValue = b[permissionSortConfig.key];

      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return permissionSortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return permissionSortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }

  // Pagination for categories
  const totalCategoryPages = Math.ceil(filteredCategories.length / categoryEntriesPerPage);
  const categoryStartIndex = (categoryCurrentPage - 1) * categoryEntriesPerPage;
  const categoryEndIndex = categoryStartIndex + categoryEntriesPerPage;
  const paginatedCategories = filteredCategories.slice(categoryStartIndex, categoryEndIndex);

  // Pagination for permissions
  const totalPermissionPages = Math.ceil(filteredPermissions.length / permissionEntriesPerPage);
  const permissionStartIndex = (permissionCurrentPage - 1) * permissionEntriesPerPage;
  const permissionEndIndex = permissionStartIndex + permissionEntriesPerPage;
  const paginatedPermissions = filteredPermissions.slice(permissionStartIndex, permissionEndIndex);

  // Generate page numbers for pagination
  const generatePageNumbers = (currentPage, totalPages) => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
              <div className={styles.panelBody}>
                <div className={styles.textCenter}>
                  <div className={styles.spinnerBorder} role="status">
                    <span className={styles.srOnly}>Loading...</span>
                  </div>
                  <p>Loading permissions...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <ol className={styles.breadcrumb}>
            <li><a href="/dashboard">Dashboard</a></li>
            <li className={styles.active}>Permissions</li>
          </ol>
        </div>
      </div>

      <div className={styles.row}>
        {/* Permissions for Categories Section */}
        <div className={styles.colMd12}>
          <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
            <h3>Permissions for Categories</h3>
          </div>

          <div className={`${styles.panelGroup} ${styles.mb20} ${styles.borderDown}`}>
            <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
              <div className={styles.panelBody}>
                <div className={styles.dataTableWrapper}>
                  <div style={{ overflow: 'hidden' }}>
                    <div className={styles.dataTablesLength} style={{ float: 'left' }}>
                      <label>
                        Show 
                        <select 
                          value={categoryEntriesPerPage} 
                          onChange={handleCategoryEntriesChange}
                          className={`${styles.formControl} ${styles.inputSm}`}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select> 
                        entries
                      </label>
                    </div>
                    <div className={styles.dataTablesFilter} style={{ float: 'right' }}>
                      <label>
                        Search:
                        <input
                          type="search"
                          className={`${styles.formControl} ${styles.inputSm}`}
                          placeholder=""
                          value={categorySearchTerm}
                          onChange={handleCategorySearch}
                        />
                      </label>
                    </div>
                  </div>

                  <table className={`${styles.dataTable} ${styles.table} ${styles.tableHover} ${styles.dtResponsive}`}>
                    <thead>
                      <tr>
                        <th
                          onClick={() => handleCategorySort("id")}
                          style={{ cursor: "pointer" }}
                        >
                          # <span style={{ marginLeft: "4px" }}>{getSortIcon("id", categorySortConfig)}</span>
                        </th>
                        <th
                          onClick={() => handleCategorySort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          Name <span style={{ marginLeft: "4px" }}>{getSortIcon("name", categorySortConfig)}</span>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCategories.map((category, index) => (
                        <tr key={category.id}>
                          <td>{categoryStartIndex + index + 1}</td>
                          <td>{category.name}</td>
                          <td>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                              onClick={() => handleShowUsers(category.id)}
                            >
                              Show users
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ overflow: 'hidden' }}>
                    <div className={styles.dataTablesInfo}>
                      Showing {categoryStartIndex + 1} to {Math.min(categoryEndIndex, filteredCategories.length)} of {filteredCategories.length} entries
                    </div>
                    <div className={styles.dataTablesPaginate}>
                      <ul className={styles.pagination}>
                        <li className={`${styles.paginateButton} ${styles.previous} ${categoryCurrentPage === 1 ? styles.disabled : ''}`}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setCategoryCurrentPage(categoryCurrentPage - 1); }}>
                            Previous
                          </a>
                        </li>
                        {generatePageNumbers(categoryCurrentPage, totalCategoryPages).map(pageNum => (
                          <li key={pageNum} className={`${styles.paginateButton} ${categoryCurrentPage === pageNum ? styles.active : ''}`}>
                            <a href="#" onClick={(e) => { e.preventDefault(); setCategoryCurrentPage(pageNum); }}>
                              {pageNum}
                            </a>
                          </li>
                        ))}
                        <li className={`${styles.paginateButton} ${styles.next} ${categoryCurrentPage === totalCategoryPages ? styles.disabled : ''}`}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setCategoryCurrentPage(categoryCurrentPage + 1); }}>
                            Next
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Permission Section */}
        <div className={styles.colMd12}>
          <div className={`${styles.panel} ${styles.panelDefault} ${styles.panelStandard}`}>
            <div className={`${styles.panelHeading} ${styles.panelTitle}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Permission</span>
                <button
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnStandard}`}
                  onClick={() => navigate('/permissions/create')}
                  style={{ marginRight: '15px' }}
                >
                  Create Permission
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              {permissions.length > 0 ? (
                <div className={styles.dataTableWrapper}>
                  <div style={{ overflow: 'hidden' }}>
                    <div className={styles.dataTablesLength} style={{ float: 'left' }}>
                      <label>
                        Show 
                        <select 
                          value={permissionEntriesPerPage} 
                          onChange={handlePermissionEntriesChange}
                          className={`${styles.formControl} ${styles.inputSm}`}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select> 
                        entries
                      </label>
                    </div>
                    <div className={styles.dataTablesFilter} style={{ float: 'right' }}>
                      <label>
                        Search:
                        <input
                          type="search"
                          className={`${styles.formControl} ${styles.inputSm}`}
                          placeholder=""
                          value={permissionSearchTerm}
                          onChange={handlePermissionSearch}
                        />
                      </label>
                    </div>
                  </div>

                  <table id="datatable-all-permissions" className={`${styles.table} ${styles.tableHover} ${styles.dtResponsive}`}>
                    <thead>
                      <tr>
                        <th
                          onClick={() => handlePermissionSort("id")}
                          style={{ cursor: "pointer" }}
                        >
                          # <span style={{ marginLeft: "4px" }}>{getSortIcon("id", permissionSortConfig)}</span>
                        </th>
                        <th
                          onClick={() => handlePermissionSort("display_name")}
                          style={{ cursor: "pointer" }}
                        >
                          Display Name <span style={{ marginLeft: "4px" }}>{getSortIcon("display_name", permissionSortConfig)}</span>
                        </th>
                        <th
                          onClick={() => handlePermissionSort("name")}
                          style={{ cursor: "pointer" }}
                        >
                          Name <span style={{ marginLeft: "4px" }}>{getSortIcon("name", permissionSortConfig)}</span>
                        </th>
                        <th className={styles.actionHead}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPermissions.map((permission, index) => (
                        <tr key={permission.id}>
                          <td>{permissionStartIndex + index + 1}</td>
                          <td>{permission.display_name}</td>
                          <td>{permission.name}</td>
                          <td>
                            <button
                              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                              onClick={() => handleEditPermission(permission.id)}
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div style={{ overflow: 'hidden' }}>
                    <div className={styles.dataTablesInfo}>
                      Showing {permissionStartIndex + 1} to {Math.min(permissionEndIndex, filteredPermissions.length)} of {filteredPermissions.length} entries
                    </div>
                    <div className={styles.dataTablesPaginate}>
                      <ul className={styles.pagination}>
                        <li className={`${styles.paginateButton} ${styles.previous} ${permissionCurrentPage === 1 ? styles.disabled : ''}`}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setPermissionCurrentPage(permissionCurrentPage - 1); }}>
                            Previous
                          </a>
                        </li>
                        {generatePageNumbers(permissionCurrentPage, totalPermissionPages).map(pageNum => (
                          <li key={pageNum} className={`${styles.paginateButton} ${permissionCurrentPage === pageNum ? styles.active : ''}`}>
                            <a href="#" onClick={(e) => { e.preventDefault(); setPermissionCurrentPage(pageNum); }}>
                              {pageNum}
                            </a>
                          </li>
                        ))}
                        <li className={`${styles.paginateButton} ${styles.next} ${permissionCurrentPage === totalPermissionPages ? styles.disabled : ''}`}>
                          <a href="#" onClick={(e) => { e.preventDefault(); setPermissionCurrentPage(permissionCurrentPage + 1); }}>
                            Next
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                <div>No permissions!</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrmPermissions; 